import React from "react";
import { Package, FileText, Download, Paperclip, AtSign, Gem, Sparkles, Users } from "lucide-react";
import { useCommunication } from "../../contexts/CommunicationContext";
import { chatService } from "../../services/chatService";

function MessageContentParser({ message, isMe = false }) {
  const { openQuickOrder, openQuickProduct, openLightbox } = useCommunication();
  const text = message.content || "";
  const attachments = Array.isArray(message.attachments)
    ? message.attachments
    : message.attachments
    ? [message.attachments]
    : [];

  // Helper para renderizar o texto com menções, pedidos e produtos interativos
  const renderFormattedText = (content) => {
    if (!content) return null;

    // Regex tokens:
    // 1. Pedidos: [71740], #71740, # 71740
    // 2. Produtos: sku.DIR-TRI, [sku.DIR-TRI], ou [DIR-TRI]
    // 3. Menções: @ia, @equipe_*, @time_*, @nome
    // 4. Links: http/https
    const regex = /(\[\s*\d{3,10}\s*\]|#\s*\d{3,10}|(?:\[)?sku\.[a-zA-Z0-9_-]+(?:\])?|\[[a-zA-Z]{2,6}-[a-zA-Z0-9_-]+\]|@(equipe_[a-zA-Z0-9_]+|time_[a-zA-Z0-9_]+|[a-zA-Z0-9_.-]+)|https?:\/\/[^\s]+)/gi;
    const parts = content.split(regex);

    return parts.map((part, idx) => {
      if (!part) return null;

      // 1. Link de Pedido [722361], #722361 ou # 722361 (apenas números)
      if (/^\[\s*\d{3,10}\s*\]$/.test(part) || /^#\s*\d{3,10}$/.test(part)) {
        const orderNum = part.replace(/[[\]#\s]/g, "");
        return (
          <button
            key={idx}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openQuickOrder(orderNum);
            }}
            className={`inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-lg font-bold text-xs border transition shadow-2xs cursor-pointer active:scale-95 ${
              isMe
                ? "bg-white/20 hover:bg-white/30 text-white border-white/30"
                : "bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200"
            }`}
            title={`Clique para ver detalhes do Pedido #${orderNum}`}
          >
            <Package size={11} className={isMe ? "text-white" : "text-indigo-600"} />
            <span>#{orderNum}</span>
          </button>
        );
      }

      // 2. Link de Produto: sku.DIR-TRI, [sku.DIR-TRI] ou [DIR-TRI]
      if (
        /^(?:\[)?sku\.[a-zA-Z0-9_-]+(?:\])?$/i.test(part) ||
        /^\[[a-zA-Z]{2,6}-[a-zA-Z0-9_-]+\]$/.test(part)
      ) {
        const rawSku = part.replace(/[[\]]/g, "").replace(/^sku\./i, "").trim();
        return (
          <button
            key={idx}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openQuickProduct(rawSku);
            }}
            className={`inline-flex items-center gap-1 mx-1 px-2 py-0.5 rounded-lg font-bold text-xs border transition shadow-2xs cursor-pointer active:scale-95 ${
              isMe
                ? "bg-emerald-400/25 hover:bg-emerald-400/35 text-emerald-100 border-emerald-300/40"
                : "bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-200"
            }`}
            title={`Clique para ver a Ficha do Produto (${rawSku})`}
          >
            <Gem size={11} className={isMe ? "text-emerald-200" : "text-emerald-600"} />
            <span>{rawSku}</span>
          </button>
        );
      }

      // 3. Menção @ia ou @assistente (IA Companion)
      if (/^@(ia|assistente)$/i.test(part)) {
        return (
          <span
            key={idx}
            className={`inline-flex items-center gap-1 mx-0.5 px-2 py-0.5 rounded-md font-bold text-xs border shadow-2xs ${
              isMe
                ? "bg-white/25 text-white border-white/40"
                : "bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-900 border-indigo-200"
            }`}
          >
            <Sparkles size={11} className={isMe ? "text-white" : "text-indigo-600"} />
            {part.substring(1)}
          </span>
        );
      }

      // 3b. Menção de Equipe @equipe_* ou @time_*
      if (/^@(equipe_[a-zA-Z0-9_]+|time_[a-zA-Z0-9_]+)$/i.test(part)) {
        const teamLabel = part
          .substring(1)
          .replace(/^(equipe_|time_)/i, "")
          .replace(/_/g, " ");

        return (
          <span
            key={idx}
            className={`inline-flex items-center gap-1 mx-0.5 px-2 py-0.5 rounded-md font-bold text-xs border shadow-2xs ${
              isMe
                ? "bg-white/25 text-white border-white/40"
                : "bg-purple-100 text-purple-900 border-purple-300"
            }`}
          >
            <Users size={11} className={isMe ? "text-white" : "text-purple-600"} />
            <span>{teamLabel.toUpperCase()}</span>
          </span>
        );
      }

      // 3c. Menção @usuario ou @operador
      if (/^@[a-zA-Z0-9_.-]+$/.test(part)) {
        return (
          <span
            key={idx}
            className={`inline-flex items-center gap-0.5 mx-0.5 px-1.5 py-0.5 rounded font-semibold text-xs border ${
              isMe
                ? "bg-white/20 text-white border-white/30"
                : "bg-blue-50 text-blue-700 border-blue-200"
            }`}
          >
            <AtSign size={10} className={isMe ? "text-white" : "text-blue-500"} />
            {part.substring(1)}
          </span>
        );
      }

      // 4. Link HTTP/HTTPS
      if (/^https?:\/\/[^\s]+$/.test(part)) {
        return (
          <a
            key={idx}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className={`hover:underline break-all font-medium ${
              isMe ? "text-indigo-100 underline decoration-indigo-300" : "text-blue-600"
            }`}
          >
            {part}
          </a>
        );
      }

      // 5. Texto comum
      return <span key={idx}>{part}</span>;
    });
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    return /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(filename);
  };

  return (
    <div className="space-y-2">
      {/* TEXTO DA MENSAGEM */}
      {text && (
        <div
          className={`text-xs md:text-sm leading-relaxed break-words whitespace-pre-wrap font-normal ${
            isMe ? "text-white" : "text-slate-800"
          }`}
        >
          {renderFormattedText(text)}
        </div>
      )}

      {/* ANEXOS / IMAGENS */}
      {attachments.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1">
          {attachments.map((file, i) => {
            const isImg = isImageFile(file);
            const fullUrl = chatService.getFileUrl(message, file);
            const thumbUrl = chatService.getFileUrl(message, file, "300x300");

            if (isImg) {
              return (
                <div
                  key={i}
                  onClick={() => openLightbox(fullUrl, file)}
                  className={`relative group rounded-xl overflow-hidden aspect-square cursor-pointer hover:shadow-md transition border ${
                    isMe
                      ? "border-white/20 bg-indigo-800/40"
                      : "border-slate-200/80 bg-slate-100"
                  }`}
                >
                  <img
                    src={thumbUrl || fullUrl}
                    alt={file}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-200"
                  />
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white">
                    <span className="text-[10px] font-semibold bg-black/60 px-2 py-1 rounded-full">
                      Ampliar
                    </span>
                  </div>
                </div>
              );
            }

            // Arquivo / Documento (PDF, Excel, etc.)
            return (
              <a
                key={i}
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                download={file}
                className={`flex items-center gap-2 p-2.5 rounded-xl transition border ${
                  isMe
                    ? "bg-white/10 hover:bg-white/20 text-white border-white/20"
                    : "bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200"
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isMe ? "bg-white/20 text-white" : "bg-indigo-50 text-indigo-600"
                  }`}
                >
                  <FileText size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{file}</p>
                  <span className={`text-[10px] ${isMe ? "text-indigo-200" : "text-slate-400"}`}>
                    Clique para baixar
                  </span>
                </div>
                <Download size={14} className={isMe ? "text-white" : "text-slate-400"} />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default React.memo(MessageContentParser);
